import { LONDON_COUNCILS } from '../councils';
import { confirmsSharedLondonScope, sharedLondonPlanningRecord, usesSharedLondonDelivery } from '../planning/londonSharedDelivery';

export const SHARED_PLANNING_ORIGIN = 'https://api.city-scope.co.uk/api/v1/core';
export class SharedLondonError extends Error {
  constructor(message: string, public status = 503) { super(message); this.name = 'SharedLondonError'; }
}
export type SharedLondonTransport = { backend?: boolean; signal?: AbortSignal; fetchImpl?: typeof fetch };
export type SharedLondonBounds = { west: number; south: number; east: number; north: number };
export type SharedLondonFeedOptions = SharedLondonTransport & {
  council?: string; query?: string; limit?: number; cursor?: string | null;
  start?: string; end?: string; bounds?: SharedLondonBounds; boundsEndExclusive?: boolean;
  filters?: Record<string, unknown>;
};

export const SHARED_LONDON_FILTERS = ['community','area_id','category_value','exclude_category_value','tags','status',
  'decision_status','postcode_prefixes','has_documents','has_forms','impact_min','impact_max','activity_from','activity_to',
  'added_from','added_to','interesting','center_lat','center_lng','radius_km','include_facets','include_total'] as const;
export const SHARED_LONDON_PROXY_FIELDS: Record<string, readonly string[]> = {
  applications: ['council','q','limit','cursor','validated_from','validated_end_exclusive','min_lng','min_lat','max_lng','max_lat',
    'bounds_end_exclusive', ...SHARED_LONDON_FILTERS],
  map: ['council','q','validated_from','validated_end_exclusive','min_lng','min_lat','max_lng','max_lat','map_aggregate_zoom',
    ...SHARED_LONDON_FILTERS.filter(key => key !== 'include_facets' && key !== 'include_total')],
  detail: ['uid'], coverage: [], summary: ['council','day','area_id'],
};

const paths: Record<string, string> = { applications: 'applications/unified-light', map: 'applications/unified-light',
  coverage: 'stats/application-counts', summary: 'stats/planning-feed-summary' };
const integer = (value: unknown) => Number.isInteger(value) && Number(value) >= 0;
const day = (value: unknown) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(Date.parse(value + 'T00:00:00Z')) && new Date(value + 'T00:00:00Z').toISOString().slice(0, 10) === value;

function requireScope(council?: string) {
  if (!usesSharedLondonDelivery(council || 'london')) throw new SharedLondonError('Shared London delivery is not released', 404);
  if (council && council !== 'london' && !Object.hasOwn(LONDON_COUNCILS, council)) throw new SharedLondonError('Unknown London council', 400);
}

function requireWindow(params: Record<string, any>) {
  const start = params.validated_from, end = params.validated_end_exclusive;
  if ((start != null && !day(start)) || (end != null && !day(end)) ||
      ((start == null) !== (end == null)) || (start && start >= end)) {
    throw new SharedLondonError('Shared London requires a complete, valid validation window', 400);
  }
}

function requireBounds(params: Record<string, any>, required = false) {
  const values = ['min_lng','min_lat','max_lng','max_lat'].map(key => params[key]);
  if (!required && values.every(value => value == null)) return;
  const [west,south,east,north] = values.map(Number);
  if (values.some(value => value == null) || ![west,south,east,north].every(Number.isFinite) ||
      !(-.7 <= west && west < east && east <= .5 && 51.1 <= south && south < north && north <= 51.9)) {
    throw new SharedLondonError('Viewport is outside the London envelope', 400);
  }
}

function confirmWindow(data: any, params: Record<string, any>) {
  if (params.validated_from != null && (data.date_basis !== 'validated' || data.window_start !== params.validated_from ||
      data.window_end_exclusive !== params.validated_end_exclusive)) throw new SharedLondonError('Shared London validation window changed');
}

async function call(kind: string, params: Record<string, any>, options: SharedLondonTransport) {
  const server = options.backend || typeof window === 'undefined';
  const target = server ? `${SHARED_PLANNING_ORIGIN}/${kind === 'detail'
    ? 'applications/by-uid/' + encodeURIComponent(params.uid) : paths[kind]}` : '/api/planning-feed/london';
  const query = new URLSearchParams(server ? {} : { kind });
  const fixed = server && kind !== 'detail' ? { country: 'england', region: 'london',
    ...(kind === 'coverage' || kind === 'summary' ? {} : { variant: kind === 'map' ? 'map' : 'light', source: 'planning' }) } : {};
  for (const [key,value] of Object.entries({ ...params, ...fixed })) {
    if (value == null || value === '' || (server && (key === 'uid' || (key === 'council' && value === 'london')))) continue;
    if (Array.isArray(value)) for (const item of value) query.append(key,String(item));
    else query.set(key,String(value));
  }
  const controller = new AbortController();
  const cancel = () => controller.abort();
  const timer = setTimeout(cancel,15000);
  if (options.signal?.aborted) cancel(); else options.signal?.addEventListener('abort',cancel,{ once:true });
  try {
    if (controller.signal.aborted) throw new SharedLondonError('Shared London request cancelled');
    const response = await (options.fetchImpl || fetch)(target + '?' + query,{ signal:controller.signal,cache:'no-store' });
    if (!response.ok) throw new SharedLondonError('Shared London planning data is unavailable',
      [400,404,429].includes(response.status) ? response.status : 503);
    return await response.json();
  } catch (error) {
    if (error instanceof SharedLondonError) throw error;
    throw new SharedLondonError('Shared London planning data is unavailable');
  } finally { clearTimeout(timer);options.signal?.removeEventListener('abort',cancel); }
}

/** Used by the strict same-origin proxy and by server-side readers. */
export async function readSharedLondonPayload(kind: string, params: Record<string, any>, options: SharedLondonTransport = {}) {
  const allowed = SHARED_LONDON_PROXY_FIELDS[kind];
  if (!allowed || Object.keys(params).some(key => !allowed.includes(key))) throw new SharedLondonError('Unsupported shared London parameter',400);
  const council = params.council;
  requireScope(kind === 'detail' ? String(params.uid || '').split(':')[0] : council);
  requireWindow(params);requireBounds(params,kind === 'map');
  if (kind === 'detail' && (typeof params.uid !== 'string' || params.uid.length > 160 || !/^[a-z-]+:[^:]{1,140}$/.test(params.uid))) {
    throw new SharedLondonError('Invalid London application identity',400);
  }
  if (params.q != null && (typeof params.q !== 'string' || params.q.length > 240)) throw new SharedLondonError('Invalid London query',400);
  if (kind === 'applications' && params.limit != null && (!Number.isInteger(Number(params.limit)) || Number(params.limit) < 1 || Number(params.limit) > 500)) {
    throw new SharedLondonError('Invalid shared London page size',400);
  }
  if (params.cursor != null && (typeof params.cursor !== 'string' || params.cursor.length > 2000 || /^\d+$/.test(params.cursor))) {
    throw new SharedLondonError('Invalid shared London cursor',400);
  }
  if (kind === 'map' && (!Number.isFinite(Number(params.map_aggregate_zoom)) || Number(params.map_aggregate_zoom) < 6 || Number(params.map_aggregate_zoom) > 21)) {
    throw new SharedLondonError('Invalid shared London map zoom',400);
  }
  const data = await call(kind,params,options);
  if (kind === 'detail') {
    if (data?.application_uid !== params.uid) throw new SharedLondonError('Shared London application identity changed');
    try { return { ...sharedLondonPlanningRecord(data,council), london_detail_loaded:true }; }
    catch { throw new SharedLondonError('Shared London application identity could not be verified'); }
  }
  if (data?.read_model !== true || data.partial === true || !confirmsSharedLondonScope(data,council)) {
    throw new SharedLondonError('Shared London scope could not be verified');
  }
  confirmWindow(data,params);
  if (kind === 'summary') {
    if (!day(params.day) || data.business_date !== params.day ||
        (data.council || null) !== (council && council !== 'london' ? council : null) ||
        (data.area_id || null) !== (params.area_id || null) ||
        !['total','today','yesterday','recent','interesting'].every(key => integer(data.counts?.[key]))) {
      throw new SharedLondonError('Shared London summary could not be verified');
    }
    return data;
  }
  if (kind === 'coverage') {
    const counts = data.counts;
    if (!counts || Object.keys(counts).length !== 33 || Object.keys(counts).some(slug => !Object.hasOwn(LONDON_COUNCILS,slug)) ||
        Object.values(counts).some(value => !integer(value)) || !integer(data.planning_applications) ||
        Object.values(counts).reduce<number>((sum,value) => sum + Number(value),0) !== data.planning_applications) {
      throw new SharedLondonError('Shared London publication counts could not be verified');
    }
    return { ...data,publication:'public',planning_delivery:'shared',
      councils:Object.entries(counts).map(([slug,applications]) => ({ slug,applications,daily_verified:null })) };
  }
  if (kind === 'map') return verifySharedMap(data,params);
  if (!Array.isArray(data.applications) || !integer(data.total) || !integer(data.count) || data.count !== data.applications.length ||
      data.count > Number(params.limit || 100) ||
      (![false,'false',0,'0'].includes(params.include_total) && data.total < data.count) ||
      !(data.next_cursor === null || (typeof data.next_cursor === 'string' && data.next_cursor.length > 0 && data.next_cursor.length <= 2000))) {
    throw new SharedLondonError('Shared London page could not be verified');
  }
  let rows: Record<string,any>[];
  try { rows = data.applications.map((record: unknown) => sharedLondonPlanningRecord(record,council)); }
  catch { throw new SharedLondonError('Shared London application identity could not be verified'); }
  if (new Set(rows.map(row => row.application_uid)).size !== rows.length || data.next_cursor === params.cursor) {
    throw new SharedLondonError('Shared London page did not advance');
  }
  if (!rows.length && data.next_cursor !== null) throw new SharedLondonError('Shared London empty page has a cursor');
  return { ...data,applications:rows,planning_delivery:'shared',facets:data.facets || {} };
}

function verifySharedMap(data: any, params: Record<string, any>) {
  const bounds = data.bounds;
  if (data.type !== 'FeatureCollection' || data.aggregation !== 'grid' || data.complete_for_bounds !== true ||
      data.bounds_end_exclusive !== true || Number(data.zoom) !== Number(params.map_aggregate_zoom) ||
      !bounds || ['min_lng','min_lat','max_lng','max_lat'].some(key => Number(bounds[key]) !== Number(params[key])) ||
      !Array.isArray(data.features) || data.features.length > 12000 || !integer(data.mapped_applications)) {
    throw new SharedLondonError('Shared London map could not be verified');
  }
  let total = 0,postcodes = 0,historical = 0,unknown = 0;
  for (const feature of data.features) {
    const p = feature?.properties, point = feature?.geometry?.coordinates, cell = p?.bounds;
    if (feature?.type !== 'Feature' || feature.geometry?.type !== 'Point' || !Array.isArray(point) || point.length !== 2 ||
        !point.every(Number.isFinite) || !Array.isArray(cell) || cell.length !== 4 || !cell.every(Number.isFinite) ||
        !(bounds.min_lng <= cell[0] && cell[0] < cell[2] && cell[2] <= bounds.max_lng &&
          bounds.min_lat <= cell[1] && cell[1] < cell[3] && cell[3] <= bounds.max_lat &&
          cell[0] <= point[0] && point[0] <= cell[2] && cell[1] <= point[1] && point[1] <= cell[3]) ||
        p.precision !== 'aggregate_cell' || !integer(p.count) || p.count < 1 || p.approximate_count !== p.count ||
        ![p.postcode_centroid_count,p.retained_historical_count,p.unknown_precision_count].every(integer) ||
        p.postcode_centroid_count + p.retained_historical_count + p.unknown_precision_count > p.count) {
      throw new SharedLondonError('Shared London map cell could not be verified');
    }
    total += p.count;postcodes += p.postcode_centroid_count;historical += p.retained_historical_count;unknown += p.unknown_precision_count;
  }
  if (total !== data.mapped_applications || postcodes !== data.postcode_centroid_applications ||
      historical !== data.retained_historical_applications || unknown !== data.unknown_precision_applications ||
      !Array.isArray(data.location_attributions) || data.location_attributions.some((line: unknown) => typeof line !== 'string' || !line.trim())) {
    throw new SharedLondonError('Shared London map counts or attribution differ');
  }
  if (postcodes > 0 && !['Contains OS data © Crown copyright and database right 2026',
    'Contains Royal Mail data © Royal Mail copyright and database right 2026',
    'Source: Office for National Statistics licensed under the Open Government Licence v.3.0'].every(line => data.location_attributions.includes(line))) {
    throw new SharedLondonError('Shared London postcode attribution could not be verified');
  }
  return { ...data,planning_delivery:'shared' };
}

export function sharedLondonFeedParameters(options: SharedLondonFeedOptions): Record<string,any> {
  if (options.filters && Object.keys(options.filters).some(key => !(SHARED_LONDON_FILTERS as readonly string[]).includes(key))) {
    throw new SharedLondonError('Unsupported shared London filter',400);
  }
  const value = { council:options.council,q:options.query,limit:options.limit,cursor:options.cursor,
    validated_from:options.start,validated_end_exclusive:options.end,
    min_lng:options.bounds?.west,min_lat:options.bounds?.south,max_lng:options.bounds?.east,max_lat:options.bounds?.north,
    bounds_end_exclusive:options.boundsEndExclusive ? true : undefined,...options.filters };
  return Object.fromEntries(Object.entries(value).filter(([,item]) => item !== undefined && item !== null && item !== ''));
}
